import { type ReactNode, useCallback } from "react";

import { cn } from "@btnet/utilities-core";

import { BdsIconPlus } from "@buildertrend/components";

import { usePermission } from "helpers/globalContext/usePermission";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { type KeyOfOrString } from "utilities/type/PropsOfType";
import { useRouteProps } from "utilities/url/url";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { getEntityDetailRoute } from "commonComponents/entity/entityLinkSelector/EntityLinkRoutes.utilities";
import { EntityLinkSelector } from "commonComponents/entity/entityLinkSelector/EntityLinkSelector";
import {
    type IEntityDetailRenderProps,
    type IEntitySelectItemExtraData,
} from "commonComponents/entity/entityLinkSelector/EntityLinkSelector.types";
import "commonComponents/entity/entityLinkSelector/EntityLinkSelectorEdit.less";

import { type EntityRecordResponse, EntityType, type SpecificationMappingData } from "handlers";

import { LinkingPanelTab } from "entity/specification/common/SpecificationsPanel/SpecificationsPanelLayout.types";
import { SpecificationStatusBadge } from "entity/specification/common/SpecificationStatus";

import "./SpecificationLinkSelectorV2.less";

import { type SpecificationStatus } from "../common/SpecificationStatus";
import { toEntityRecord } from "./SpecificationLinkSelectorV2.utilities";

const Specification = lazyLoadComponent(() => import("entity/specification/Specification"));

interface ISpecificationLinkSelectorV2Props<FormValues> {
    /**
     * The external ID of the source entity (e.g., Purchase Order GUID)
     */
    sourceExternalId: string;
    /**
     * The entity type of the source (e.g., EntityType.PurchaseOrder)
     */
    sourceEntityType: EntityType;
    /**
     * The job ID for filtering specifications and routing
     */
    jobId: number;
    /**
     * Whether the selector is in read-only mode (no editing allowed)
     * When true, shows only RelatedItemTags with no interaction
     * When false, shows multi-select dropdown with add/edit capabilities
     * @default false
     */
    isReadOnly?: boolean;
    /**
     * Whether user can add new specifications
     * @default false
     */
    canAdd?: boolean;
    /**
     * Whether user can edit existing specifications
     * @default false
     */
    canEdit?: boolean;
    /**
     * Whether user can remove linked specifications
     * @default true
     */
    canRemove?: boolean;
    /**
     * Field path for testing and form integration
     */
    fieldPath?: KeyOfOrString<FormValues>;
    /**
     * Use BDS components instead of BT components
     * @default true
     */
    useBds?: boolean;
    /**
     * Additional class name applied to the select dropdown overlay.
     * Useful for setting z-index when inside a drawer.
     */
    dropdownClassName?: string;
    /**
     * Optional helper text to display below the selector
     */
    helperText?: string;
    /**
     * Optional mapping data for pre-populating new specification content from estimate group data
     */
    specificationMapping?: SpecificationMappingData;

    /**
     * When provided, the "+ Spec" button invokes this callback instead of pushing the modal
     * route. The parent owns rendering the inline create surface and linking the new spec.
     */
    onCreateInline?: () => void;

    /**
     * Callback invoked when the number of linked specifications changes.
     */
    onSpecLinkedCountChange?: (count: number) => void;

    /**
     * Callback invoked when the number of linked plan sets changes.
     */
    onPlanSetLinkedCountChange?: (count: number) => void;

    /**
     * Whether plan set linking is supported for the source entity.
     * When false, the PlanSet EntityLinkSelector is not rendered and no PlanSet fetch occurs.
     */
    showPlanSets?: boolean;

    currentLinkingTab?: LinkingPanelTab;

    /**
     * Passed through to EntityLinkSelector to control the React Query cache key.
     * Pass the same array from co-located components (e.g. SpecificationsLinkButton) so they share
     * a single cache entry. Defaults to [targetEntityType] inside EntityLinkSelector when omitted.
     */
    queryKeyEntityTypes?: EntityType[];

    /**
     * When provided, both inner selectors call this in place of the single-source PUT.
     * Used by mass-link flows that persist via a separate endpoint on Save.
     */
    onPersistLinksOverride?: (targetEntityType: EntityType, ids: string[]) => void;

    /** General-job specs (e.g. Cost Group): suppresses the Draft/Published badge on dropdown items and chips. */
    isGeneralJob?: boolean;
}

/**
 * Specification-specific wrapper for EntityLinkSelector
 * Provides backward-compatible API while using generic implementation
 */
export const SpecificationLinkSelectorV2 = <FormValues,>(
    props: ISpecificationLinkSelectorV2Props<FormValues>,
) => {
    const {
        canAdd: canAddProp = false,
        specificationMapping,
        jobId,
        onCreateInline,
        onSpecLinkedCountChange,
        onPlanSetLinkedCountChange,
        showPlanSets = false,
        currentLinkingTab,
        isGeneralJob = false,
        ...restProps
    } = props;

    // Check if user has permission to add specifications
    const hasAddPermission = usePermission("specifications", "canAdd");
    const canAdd = canAddProp && hasAddPermission;

    const { match, location, history } = useRouteProps();

    const handleAddClick = useCallback(() => {
        if (onCreateInline) {
            onCreateInline();
            return;
        }
        const addPath =
            match.url + getEntityDetailRoute(EntityType.Specification, 0, jobId) + location.search;
        history.push(addPath, { edit: true });
    }, [onCreateInline, match.url, jobId, location.search, history]);

    // Status badges on dropdown items + chips in edit mode; suppressed for general-job specs.
    const renderItemWithStatus = useCallback(
        (item: BTSelectItem<IEntitySelectItemExtraData>): ReactNode => {
            const status = item.extraData?.status;

            if (isGeneralJob || status == null) {
                return item.title;
            }

            return (
                <div className="entity-link-selector-item-with-status">
                    <span>{item.title}</span>
                    <SpecificationStatusBadge status={status as SpecificationStatus} />
                </div>
            );
        },
        [isGeneralJob],
    );

    // Custom render function for status badges in readonly mode (tags). Suppressed for general-job specs.
    const renderStatusBadge = useCallback(
        (entity: EntityRecordResponse): ReactNode | undefined => {
            if (isGeneralJob || entity.status == null) {
                return undefined;
            }

            return <SpecificationStatusBadge status={entity.status as SpecificationStatus} />;
        },
        [isGeneralJob],
    );

    // Define specification-specific detail renderer
    const renderSpecificationDetail = useCallback(
        (detailProps: IEntityDetailRenderProps<IEntitySelectItemExtraData>) => {
            return (
                <Specification
                    {...detailProps.routeProps}
                    jobId={detailProps.jobId}
                    modalConfig={detailProps.modalConfig}
                    id={detailProps.id}
                    onAdd={(spec) => detailProps.add(toEntityRecord(spec))}
                    onEdit={(spec) => detailProps.edit(toEntityRecord(spec))}
                    onDelete={(spec) => detailProps.delete(toEntityRecord(spec))}
                    specificationMapping={detailProps.id === 0 ? specificationMapping : undefined}
                />
            );
        },
        [specificationMapping],
    );

    return (
        <div className="SpecificationLinkSelectorV2">
            {showPlanSets && (
                <div
                    className={cn("plan-link-row", {
                        "plan-link-row--hidden": currentLinkingTab === LinkingPanelTab.Specs,
                    })}
                >
                    <EntityLinkSelector<FormValues, IEntitySelectItemExtraData>
                        {...restProps}
                        canAdd={false}
                        jobId={jobId}
                        targetEntityType={EntityType.PlanSet}
                        renderDetail={renderSpecificationDetail}
                        customItemRender={renderItemWithStatus}
                        renderStatus={renderStatusBadge}
                        onLinkedCountChange={onPlanSetLinkedCountChange}
                    />
                </div>
            )}
            <div
                className={cn("spec-link-row", {
                    "spec-link-row--hidden": currentLinkingTab === LinkingPanelTab.Plans,
                })}
            >
                <div className="spec-link-selector">
                    <EntityLinkSelector<FormValues, IEntitySelectItemExtraData>
                        {...restProps}
                        jobId={jobId}
                        canAdd={false}
                        targetEntityType={EntityType.Specification}
                        renderDetail={renderSpecificationDetail}
                        customItemRender={renderItemWithStatus}
                        renderStatus={renderStatusBadge}
                        onLinkedCountChange={onSpecLinkedCountChange}
                    />
                </div>
                {canAdd && (
                    <BTButton
                        icon={<BdsIconPlus />}
                        onClick={handleAddClick}
                        type="secondary"
                        data-testid="add-specification"
                    >
                        Spec
                    </BTButton>
                )}
            </div>
        </div>
    );
};
